/**
 * `useContactPeerNames` — a `(channel, peerId) → contact identity` index for the
 * agent IM tabs (Telegram / WhatsApp). The peer-keyed twin of `useContactNames`
 * (the email tab): the contacts store is the authoritative source for what a
 * correspondent is CALLED, so a stranger renders as the name the lawyer gave
 * them instead of a raw `telegram:<chatid>` coordinate.
 *
 * Deliberately forgiving — contacts are a NICETY here, never a dependency. If
 * `contacts.list` is missing on an older runtime, or fails, or the gateway is
 * still connecting, the hook returns an empty index and every caller degrades
 * to the self-asserted `peerName`, then the raw peer, then "unknown sender". It
 * must never be able to stop the IM tabs from rendering.
 */
import { useEffect, useMemo, useState } from "react";

import { useGatewayStore } from "@/stores/useGatewayStore";
import { buildContactPeerNameIndex, peerKey, type PeerContact } from "@/lib/peer-identity";
import type { Contact } from "@/types/contacts";

export type { PeerContact } from "@/lib/peer-identity";

export function useContactPeerNames(): {
  /** `peerKey(channel, peerId)` → curated identity. Empty when unavailable. */
  index: Map<string, PeerContact>;
  /** Convenience lookup with the same fallback semantics; `undefined` for a
   *  null/empty peer (the `peer:null` stash-miss bucket) or an unmatched one. */
  resolve: (channel: string, peer: string | null | undefined) => PeerContact | undefined;
} {
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const [contacts, setContacts] = useState<Contact[]>([]);

  useEffect(() => {
    if (!rpc || connectionState !== "connected") return;
    let cancelled = false;
    // Archived contacts are excluded (contactsList defaults includeArchived
    // false): an archived correspondent should not keep naming a live peer.
    rpc
      .contactsList({})
      .then((res) => {
        if (!cancelled) setContacts(res.contacts ?? []);
      })
      .catch(() => {
        // Method-not-found on an older runtime, or any transport failure —
        // names are optional, so swallow and fall back to the raw peer.
        if (!cancelled) setContacts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [rpc, connectionState]);

  const index = useMemo(() => buildContactPeerNameIndex(contacts), [contacts]);
  const resolve = useMemo(
    () =>
      (channel: string, peer: string | null | undefined): PeerContact | undefined => {
        if (!peer || !peer.trim()) return undefined;
        return index.get(peerKey(channel, peer));
      },
    [index],
  );
  return { index, resolve };
}
